import { useCallback, useEffect, useRef, useState } from 'react';

import { createAdminSubscriptionPlan, getAdminSubscriptionsOverview, type AdminSubscriptionsOverview } from '../api/subscription';

export const useAdminSubscriptions = (productCode?: string) => {
  const [overview, setOverview] = useState<AdminSubscriptionsOverview | null>(null);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [creatingPlan, setCreatingPlan] = useState(false);

  const refreshingRef = useRef(false);

  const refreshOverview = useCallback(async () => {
    if (refreshingRef.current) return;
    refreshingRef.current = true;
    setLoadingOverview(true);
    try {
      const response = await getAdminSubscriptionsOverview(productCode);
      setOverview(response.data.data);
      return response.data.data;
    } finally {
      setLoadingOverview(false);
      refreshingRef.current = false;
    }
  }, [productCode]);

  useEffect(() => {
    refreshOverview().catch(() => setOverview(null));
  }, [refreshOverview]);

  const createPlan = useCallback(async (payload: {
    productCode?: string;
    code: string;
    name: string;
    description?: string;
    amount: number;
    currency?: string;
    intervalUnit: string;
    intervalCount?: number;
    isDefault?: boolean;
    isPublic?: boolean;
    isPopular?: boolean;
    showOnLanding?: boolean;
    features?: Record<string, unknown>;
  }) => {
    setCreatingPlan(true);
    try {
      await createAdminSubscriptionPlan({
        ...payload,
        productCode: payload.productCode || productCode,
      });
      refreshOverview().catch(() => undefined);
    } finally {
      setCreatingPlan(false);
    }
  }, [productCode, refreshOverview]);

  return {
    overview,
    loadingOverview,
    creatingPlan,
    refreshOverview,
    createPlan,
  };
};
